import {
  CalendarClock,
  CheckCircle2,
  FolderOpen,
  Loader2,
} from "lucide-react";
import type { Job } from "../types";
import {
  countJobsByCategory,
  JOB_CATEGORY_LABELS,
  JOB_CATEGORY_ORDER,
} from "../utils/jobCategories";
import type { JobCategory } from "../utils/jobCategories";

const CATEGORY_META: Record<
  JobCategory,
  {
    icon: typeof FolderOpen;
    subtitle: string;
    cardClass: string;
    iconClass: string;
    badgeClass: string;
  }
> = {
  to_schedule: {
    icon: CalendarClock,
    subtitle: "À faire (avec ou sans date)",
    cardClass:
      "from-amber-50 to-orange-50/80 border-amber-200/70 dark:from-amber-500/10 dark:to-orange-500/5 dark:border-amber-500/25 hover:border-amber-300 dark:hover:border-amber-400/40",
    iconClass: "text-amber-600 dark:text-amber-400 bg-amber-100/80 dark:bg-amber-500/20",
    badgeClass: "bg-amber-600 dark:bg-amber-500",
  },
  in_progress: {
    icon: Loader2,
    subtitle: "Chantiers démarrés",
    cardClass:
      "from-blue-50 to-indigo-50/80 border-blue-200/70 dark:from-blue-500/10 dark:to-indigo-500/5 dark:border-blue-500/25 hover:border-blue-300 dark:hover:border-blue-400/40",
    iconClass: "text-blue-600 dark:text-blue-400 bg-blue-100/80 dark:bg-blue-500/20",
    badgeClass: "bg-blue-600 dark:bg-blue-500",
  },
  done: {
    icon: CheckCircle2,
    subtitle: "Clôturés ou annulés",
    cardClass:
      "from-emerald-50 to-green-50/80 border-emerald-200/70 dark:from-emerald-500/10 dark:to-green-500/5 dark:border-emerald-500/25 hover:border-emerald-300 dark:hover:border-emerald-400/40",
    iconClass: "text-emerald-600 dark:text-emerald-400 bg-emerald-100/80 dark:bg-emerald-500/20",
    badgeClass: "bg-emerald-600 dark:bg-emerald-500",
  },
  all: {
    icon: FolderOpen,
    subtitle: "Vue complète",
    cardClass:
      "from-slate-50 to-gray-50/80 border-gray-200/80 dark:from-slate-800/60 dark:to-slate-900/40 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20",
    iconClass: "text-gray-600 dark:text-slate-300 bg-gray-100/80 dark:bg-slate-700/60",
    badgeClass: "bg-gray-700 dark:bg-slate-500",
  },
};

/** Grille 2×2 / 4 colonnes (partenaire). `rows` = cartes pleine largeur (poseur mobile). */
export type JobsCategoryHubLayout = "grid" | "rows";

const ROW_SHORT_LABELS: Record<JobCategory, string> = {
  to_schedule: "À programmer",
  in_progress: "En cours",
  done: "Terminé",
  all: "Tous",
};

export interface JobsCategoryHubProps {
  jobs: Job[];
  accent?: "indigo" | "blue";
  /** `rows` : liste verticale lisible sur écran étroit (poseurs). */
  layout?: JobsCategoryHubLayout;
  onSelect: (category: JobCategory) => void;
}

export function JobsCategoryHub({
  jobs,
  accent = "indigo",
  layout = "grid",
  onSelect,
}: JobsCategoryHubProps) {
  const counts = countJobsByCategory(jobs);
  const ringAccent =
    accent === "blue"
      ? "focus-visible:ring-blue-500/40"
      : "focus-visible:ring-indigo-500/40";

  const isRows = layout === "rows";

  return (
    <section className="space-y-3" aria-label="Catégories de dossiers">
      <p className="text-sm text-gray-500 dark:text-slate-400 px-0.5 leading-snug">
        Choisissez une catégorie pour afficher vos dossiers
      </p>
      <div
        className={
          isRows
            ? "flex flex-col gap-2.5"
            : "grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
        }
      >
        {JOB_CATEGORY_ORDER.map((category) => {
          const meta = CATEGORY_META[category];
          const Icon = meta.icon;
          const count = counts[category];
          const isEmpty = count === 0 && category !== "all";
          const label = isRows ? ROW_SHORT_LABELS[category] : JOB_CATEGORY_LABELS[category];

          const badge = (
            <span
              className={`shrink-0 min-w-[2rem] h-8 px-2.5 inline-flex items-center justify-center rounded-full text-[13px] font-bold text-white tabular-nums shadow-sm ${meta.badgeClass}`}
            >
              {count}
            </span>
          );

          if (isRows) {
            return (
              <button
                key={category}
                type="button"
                onClick={() => onSelect(category)}
                aria-label={`${JOB_CATEGORY_LABELS[category]}, ${count} dossier${count !== 1 ? "s" : ""}`}
                className={`group w-full flex items-center gap-3 text-left p-3.5 rounded-2xl border bg-gradient-to-br shadow-sm transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 ${ringAccent} ${meta.cardClass} ${
                  isEmpty ? "opacity-80" : ""
                }`}
              >
                <div
                  className={`flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${meta.iconClass}`}
                >
                  <Icon
                    size={22}
                    strokeWidth={2}
                    className={category === "in_progress" ? "group-hover:animate-spin" : undefined}
                  />
                </div>
                <div className="flex-1 min-w-0 py-0.5">
                  <span className="block text-[15px] font-semibold text-gray-900 dark:text-white tracking-tight leading-tight">
                    {label}
                  </span>
                  <span className="block text-[12px] text-gray-500 dark:text-slate-400 mt-0.5 leading-snug truncate">
                    {meta.subtitle}
                  </span>
                </div>
                {badge}
              </button>
            );
          }

          return (
            <button
              key={category}
              type="button"
              onClick={() => onSelect(category)}
              aria-label={`${JOB_CATEGORY_LABELS[category]}, ${count} dossier${count !== 1 ? "s" : ""}`}
              className={`group relative flex flex-col items-start text-left p-4 md:p-5 pr-12 rounded-2xl border bg-gradient-to-br shadow-sm transition-all duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 ${ringAccent} ${meta.cardClass} ${
                isEmpty ? "opacity-75" : ""
              }`}
            >
              <div
                className={`flex items-center justify-center w-11 h-11 rounded-xl mb-3 shrink-0 ${meta.iconClass}`}
              >
                <Icon
                  size={22}
                  strokeWidth={2}
                  className={category === "in_progress" ? "group-hover:animate-spin" : undefined}
                />
              </div>
              <span className="w-full text-[13px] sm:text-[15px] md:text-base font-semibold text-gray-900 dark:text-white tracking-tight leading-snug break-words hyphens-auto">
                {label}
              </span>
              <span className="w-full text-[11px] sm:text-[12px] text-gray-500 dark:text-slate-400 mt-1 leading-snug line-clamp-2">
                {meta.subtitle}
              </span>
              <span
                className={`absolute top-3 right-3 min-w-[1.75rem] h-7 px-2 inline-flex items-center justify-center rounded-full text-[12px] font-bold text-white tabular-nums shadow-sm ${meta.badgeClass}`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export { JOB_CATEGORY_LABELS };
export type { JobCategory };
